<template>
  <div>
    <!-- 검색 영역 - PC/Mobile 공통 -->
    <vx-card class="search-section">
      <div class="flex items-center gap-4">
        <search-keyword label="" v-model="keyword" />
        <vs-button class="py-2 px-6" color="secondary" @click="loadPostList">검색</vs-button>
      </div>
    </vx-card>

    <!-- 헤더 영역 - PC/Mobile 공통 -->
    <div class="mt-4 flex justify-between items-center header-section">
      <div style="font-size: 21px; font-weight: 500">
        총 게시글 <span style="color: #7e72f2">{{ total }}</span
        >건
      </div>
      <div>
        <vs-button v-if="permission.c && permission.u && permission.d" class="py-2 px-6" color="secondary" @click="onClickWrite">강의 업로드</vs-button>
      </div>
    </div>

    <!-- PC 레이아웃 -->
    <vx-card class="mt-4 body-table desktop-layout">
      <!-- tool -->
      <div class="p-6 grid sm:flex items-center gap-4">
        <!-- perPage -->
        <vs-select v-model="perPage" @input="changePerPage">
          <vs-select-item v-for="pp in [10, 30]" :key="`perPage_${pp}`" :value="pp" :text="`${pp}개씩 보기`" />
        </vs-select>

        <!-- sort -->
        <div class="flex gap-4 items-center" style="font-size: 17px; color: #555555">
          <span style="color: #1d1d1d; font-weight: 700">정렬기준</span>
          <a
            v-for="sortData in ['최신순', '가나다순']"
            :key="`sort_${sortData}`"
            :class="{ 'sort-active': sort === sortData }"
            href="javascript:;"
            @click="
              () => {
                sort = sortData;
                loadPostList();
              }
            "
            >{{ sortData }}</a
          >
        </div>
      </div>

      <!-- table -->
      <div class="overflow-x-auto">
        <table class="w-full">
          <colgroup>
            <col style="width: 96px; min-width: 96px" />
            <col style="width: 180px; min-width: 180px" />
            <col style="width: 180px; min-width: 180px" />
            <col style="min-width: 300px" />
            <col style="width: 180px; min-width: 180px" />
            <col style="width: 180px; min-width: 180px" />
            <col style="width: 100px; min-width: 100px" />
            <col v-if="permission.u" style="width: 120px; min-width: 120px" />
          </colgroup>
          <tr>
            <th>No.</th>
            <th>교육대상</th>
            <th>교육종류</th>
            <th>강의명</th>
            <th>첨부파일</th>
            <th>업로드일시</th>
            <th>강의시간</th>
            <th v-if="permission.u">수강인원</th>
          </tr>

          <tr v-for="(item, index) in list" :key="`pc_${index}`" class="cursor-pointer" @click="onClickDetail(item)">
            <td>{{ (page - 1) * perPage + index + 1 }}</td>
            <td>{{ item.body.교육대상 }}</td>
            <td>{{ getVideoCodeText(item.body.videoCode) }}</td>
            <td>{{ item.body.제목 }}</td>
            <td>
              <div class="flex items-center justify-center">
                <vs-tooltip v-for="file in item.body.fileList" :key="`file_${file.path}`" :text="file.fileName || '{이름없음}'" placement="top">
                  <feather-icon icon="FileIcon" style="color: #7e72f2; cursor: pointer; width: 20px" @click.stop="onClickFile(file)" />
                </vs-tooltip>
              </div>
            </td>
            <td>{{ item.created_at }}</td>
            <td>{{ formatDuration(item.body.duration) }}</td>
            <td v-if="permission.u">{{ item.수강인원 }}</td>
          </tr>
        </table>
      </div>

      <!-- padding -->
      <div class="py-6 vs-con-table">
        <div class="con-pagination-table vs-table--pagination">
          <k-pagination :total="lastPage || 1" :value="page" @input="changePage"></k-pagination>
        </div>
      </div>
    </vx-card>

    <!-- 모바일 레이아웃 -->
    <div class="mobile-layout mt-4">
      <!-- 정렬 및 필터 -->
      <vx-card class="mobile-filter">
        <div class="flex items-center justify-between gap-4 p-4">
          <!-- 정렬 기준 -->
          <div class="flex gap-3 items-center">
            <span class="text-sm font-semibold text-gray-700">정렬</span>
            <select v-model="sort" @change="loadPostList" class="mobile-select">
              <option value="최신순">최신순</option>
              <option value="가나다순">가나다순</option>
            </select>
          </div>
        </div>
      </vx-card>

      <!-- 카드 컨테이너 -->
      <div v-if="list.length > 0" class="mobile-cards-container">
        <div class="mobile-cards-wrapper" ref="cardsWrapper" @touchstart="handleTouchStart" @touchmove="handleTouchMove" @touchend="handleTouchEnd" :style="{ transform: `translateX(-${currentCardIndex * 100}%)` }">
          <div v-for="(item, index) in displayedList" :key="`mobile_${index}`" class="mobile-card" @click="onClickDetail(item)">
            <div class="card-header">
              <div class="card-number">{{ (currentPage - 1) * mobileCardsPerPage + index + 1 }}</div>
              <div class="card-target">{{ item.body.교육대상 }}</div>
            </div>

            <div class="card-title">{{ item.body.제목 }}</div>

            <div class="card-info">
              <div class="info-row">
                <feather-icon icon="CalendarIcon" size="16" class="info-icon" />
                <span class="info-label">업로드일시:</span>
                <span class="info-value">{{ item.created_at }}</span>
              </div>
              <div class="info-row">
                <feather-icon icon="ClockIcon" size="16" class="info-icon" />
                <span class="info-label">강의시간:</span>
                <span class="info-value">{{ formatDuration(item.body.duration) }}</span>
              </div>
              <div v-if="permission.u" class="info-row">
                <feather-icon icon="UsersIcon" size="16" class="info-icon" />
                <span class="info-label">수강인원:</span>
                <span class="info-value">{{ item.수강인원 }}명</span>
              </div>
            </div>

            <div v-if="item.body.fileList && item.body.fileList.length > 0" class="card-files">
              <div class="files-header">
                <feather-icon icon="PaperclipIcon" size="16" />
                <span class="files-label">첨부파일</span>
                <span class="files-count">({{ item.body.fileList.length }}개)</span>
              </div>
              <div class="files-list">
                <div v-for="file in item.body.fileList.slice(0, 3)" :key="`mobile_file_${file.path}`" class="file-item" @click.stop="onClickFile(file)">
                  <feather-icon icon="FileIcon" size="14" />
                  <span>{{ file.fileName || "{이름없음}" }}</span>
                </div>
                <div v-if="item.body.fileList.length > 3" class="file-more">+{{ item.body.fileList.length - 3 }}개 더보기</div>
              </div>
            </div>
          </div>
        </div>

        <!-- 카드 인디케이터 -->
        <div class="card-indicators">
          <div v-for="(item, index) in displayedList" :key="`indicator_${index}`" :class="['indicator', { active: index === currentCardIndex }]" @click="goToCard(index)"></div>
        </div>

        <!-- 더보기 버튼 -->
        <div v-if="hasMoreCards" class="load-more-container">
          <vs-button class="load-more-btn" color="secondary" type="border" @click="loadMoreCards" :loading="loadingMore"> 더보기 ({{ remainingCards }}개) </vs-button>
        </div>
      </div>

      <!-- 빈 목록 -->
      <vx-card v-else class="empty-state">
        <div class="empty-content">
          <feather-icon icon="BookOpenIcon" size="48" class="empty-icon" />
          <h3>등록된 교육이 없습니다</h3>
          <p>새로운 교육을 업로드해보세요.</p>
        </div>
      </vx-card>
    </div>

    <vs-popup id="detailPopup" :active.sync="openPostDetail" title="안전보건 교육 수강" @close="closePopup" :style="{ zIndex: 53000 }">
      <detail ref="DetailPage" :data="selectPost" :permission="permission" @edit="onClickEdit" @delete="onClickDelete" />
    </vs-popup>
  </div>
</template>

<script>
import SearchKeyword from "@/views/list/components/SearchKeyword.vue";
import KPagination from "@/components/kPagination/kPagination.vue";
import Detail from "@/views/boards/single-page/1064/Detail.vue";
import * as DataUtil from "@/util/DataUtil";
import * as UiUtil from "@/util/UiUtil";
import {getVideoCodeText} from "@/util/TrainingVideo";

export default {
  components: {
    Detail,
    KPagination,
    SearchKeyword,
  },
  computed: {
    projectId() {
      return this.$route.params.proj_id;
    },
    boardId() {
      return this.$route.params.boardId;
    },
    myInfo() {
      return this.$store.state.auth.userInfo;
    },
    displayedList() {
      return this.list.slice(0, this.currentPage * this.mobileCardsPerPage);
    },
    hasMoreCards() {
      return this.displayedList.length < this.total;
    },
    remainingCards() {
      return this.total - this.displayedList.length;
    },
  },
  data() {
    return {
      permission: { c: 0, r: 0, u: 0, d: 0 }, // 게시판 권한
      page: 1, // 현재 페이지 (PC용)
      perPage: 10, // 페이지당 게시글 수
      lastPage: 1, // 마지막 페이지
      total: 0, // 총 게시글 수
      keyword: "", // 검색 키워드
      sort: "최신순",
      list: [], // 게시글 목록

      selectPost: null, // 선택된 게시글
      openPostDetail: false, // 상세보기 열림 여부

      // 모바일 전용
      currentCardIndex: 0, // 현재 카드 인덱스
      currentPage: 1, // 모바일 페이지
      mobileCardsPerPage: 5, // 모바일에서 한 번에 로드할 카드 수
      loadingMore: false, // 더보기 로딩 상태

      // 터치 관련
      touchStartX: 0,
      touchEndX: 0,
      isDragging: false,
    };
  },
  async mounted() {
    await this.loadPermission();
    await this.loadPostList();
  },
  methods: {
    getVideoCodeText,
    async loadPermission() {
      try {
        UiUtil.loading(true)
        if (Number.isInteger(parseInt(this.boardId))) {
          let boards = this.$store.state.member.memberInfo.boards;
          if (!boards) {
            await this.$store.dispatch("member/LOAD_MEMBER_DETAIL");
            boards = this.$store.state.member.memberInfo.boards;
          }

          let board = boards.find((b) => parseInt(b.id) === parseInt(this.boardId));
          if (!!board && !!board.pivot) {
            if (board.pivot.project_id) {
              // 프로젝트에 맞는 권한이 있는지 다시 조사한다
              board = boards.find((b) => parseInt(b.pivot.board_id) === parseInt(this.boardId) && parseInt(b.pivot.project_id) === parseInt(this.projectId));
              this.permission = { ...board.pivot };
            } else {
              this.permission = { ...board.pivot };
            }
          }
        }
      } catch (error) {
      } finally {
        UiUtil.loading(false)
      }
    },

    async loadPostList() {
      try {
        UiUtil.loading(true)
        const projectId = this.projectId;
        const boardId = this.boardId;
        const page = this.page;
        const perPage = this.perPage;
        const sort = this.sort;
        const keyword = this.keyword.trim().toLowerCase();
        const conditions = [];
        const orders = [];

        if (keyword) {
          conditions.push({ key: "body.교육대상", operator: "LIKE", value: `%${keyword}%` });
          conditions.push({ key: "body.제목", operator: "LIKE", value: `%${keyword}%`, boolean: "OR" });
        }

        if (sort === "최신순") {
          orders.push({ target: "id", direction: "desc" });
        } else if (sort === "가나다순") {
          orders.push({ target: "$body.제목", direction: "asc" });
        }

        const listInfo = await this.$store.dispatch("board/LOAD_POST_LIST", {
          projectId,
          boardId,
          page,
          perPage,
          conditions,
          orders,
        });

        this.lastPage = listInfo.last_page;
        this.total = listInfo.total;
        const list = listInfo.data.map((item) => {
          const content = JSON.parse(item.post_content);
          return {
            ...item,
            header: content.header,
            body: content.body,
          };
        });

        if (list.length > 0) {
          for (const item of list) {
            item.수강인원 = await this.get수강인원(item.id);
          }
        }

        this.list = list;
        this.currentCardIndex = 0; // 카드 인덱스 초기화
      } catch (e) {

      } finally {
        UiUtil.loading(false)
      }
    },

    async loadMoreCards() {
      if (this.loadingMore || !this.hasMoreCards) return;

      this.loadingMore = true;
      this.currentPage++;

      // 추가 데이터 로드
      const projectId = this.projectId;
      const boardId = this.boardId;
      const page = this.currentPage;
      const perPage = this.mobileCardsPerPage;
      const sort = this.sort;
      const keyword = this.keyword.trim().toLowerCase();
      const conditions = [];
      const orders = [];

      if (keyword) {
        conditions.push({ key: "body.교육대상", operator: "LIKE", value: `%${keyword}%` });
        conditions.push({ key: "body.제목", operator: "LIKE", value: `%${keyword}%`, boolean: "OR" });
      }

      if (sort === "최신순") {
        orders.push({ target: "id", direction: "desc" });
      } else if (sort === "가나다순") {
        orders.push({ target: "$body.제목", direction: "asc" });
      }

      try {
        const listInfo = await this.$store.dispatch("board/LOAD_POST_LIST", {
          projectId,
          boardId,
          page,
          perPage,
          conditions,
          orders,
        });

        const newList = listInfo.data.map((item) => {
          const content = JSON.parse(item.post_content);
          return {
            ...item,
            header: content.header,
            body: content.body,
          };
        });

        // 수강인원 정보 추가
        if (newList.length > 0) {
          for (const item of newList) {
            item.수강인원 = await this.get수강인원(item.id);
          }
        }

        this.list = [...this.list, ...newList];
      } catch (error) {
        console.error("추가 데이터 로드 실패:", error);
      } finally {
        this.loadingMore = false;
      }
    },

    async get수강인원(postId) {
      const listInfo = await this.$store.dispatch("board/LOAD_POST_LIST", {
        projectId: this.projectId,
        boardId: 1065,
        page: 1,
        perPage: 1,
        conditions: [{ key: "body.videoPostId", operator: "=", value: postId }],
      });
      if (listInfo) {
        return listInfo.total;
      }
      return 0;
    },

    formatDuration(seconds) {
      const m = Math.floor(seconds / 60);
      const s = Math.floor(seconds % 60)
        .toString()
        .padStart(2, "0");
      return `${m}:${s}`;
    },
    changePage(page) {
      this.page = page;
      this.loadPostList();
    },
    changePerPage() {
      this.page = 1;
      this.loadPostList();
    },

    // 터치 이벤트 핸들러
    handleTouchStart(e) {
      this.touchStartX = e.touches[0].clientX;
      this.isDragging = true;
    },
    handleTouchMove(e) {
      if (!this.isDragging) return;
      this.touchEndX = e.touches[0].clientX;
    },
    handleTouchEnd() {
      if (!this.isDragging) return;
      this.isDragging = false;

      const deltaX = this.touchStartX - this.touchEndX;
      const threshold = 50; // 최소 스와이프 거리

      if (Math.abs(deltaX) > threshold) {
        if (deltaX > 0 && this.currentCardIndex < this.displayedList.length - 1) {
          // 왼쪽으로 스와이프 (다음 카드)
          this.currentCardIndex++;
        } else if (deltaX < 0 && this.currentCardIndex > 0) {
          // 오른쪽으로 스와이프 (이전 카드)
          this.currentCardIndex--;
        }
      }
    },
    goToCard(index) {
      this.currentCardIndex = index;
    },

    async onClickDetail(data) {
      UiUtil.loading(true);
      // 수강내역에 등록된 문서인지 검색한다.
      await this.$store
        .dispatch("board/LOAD_POST_LIST", {
          projectId: this.projectId,
          boardId: 1065,
          conditions: [
            { key: "user_id", operator: "=", value: this.myInfo.id },
            { key: "body.videoPostId", operator: "=", value: data.id },
          ],
          page: 1,
          perPage: 1,
        })
        .then((res) => {
          if (res.data.length > 0) {
            data.수강내역 = res.data[0];
          }
        });
      UiUtil.loading(false);

      this.selectPost = data;
      this.openPostDetail = true;

      this.$nextTick(() => {
        if (data.body.videoId) {
          this.$refs.DetailPage.start();
        }
      });
    },
    onClickFile(file) {
      if (file && file.fileName && file.path) {
        DataUtil.downloadFile(file.fileName, file.path);
      } else {
        alert("파일 정보가 올바르지 않습니다.");
      }
    },
    onClickWrite() {
      const proj_id = this.$route.params.proj_id;
      const boardId = `${this.boardId}`;
      const query = this.$route.query || {};
      this.$router.push({ name: "write|tab", params: { pageType: "write", proj_id, boardId }, query: { ...query, _t_back: "1064/page" } });
    },
    onClickEdit(data) {
      this.$refs.DetailPage.pause();
      this.openPostDetail = false;

      const query = this.$route.query || {};

      this.$nextTick(() => {
        this.$router.push({
          name: "write|update|tab",
          params: {
            pageType: "write",
            proj_id: this.projectId,
            boardId: this.boardId,
            postId: data.id,
          },
          query: {
            ...query,
            _t_back: "1064/page",
          },
        });
      });
    },
    onClickDelete(data) {
      this.$vs.dialog({
        type: "confirm",
        title: "교육 수강 삭제",
        text: "정말로 해당 교육 수강을 삭제하시겠습니까?",
        acceptText: "예",
        cancelText: "아니오",
        accept: async () => {
          // 관련 수강내역 모두 삭제
          const videoPostId = data.id;
          const list = await this.$store.dispatch("board/LOAD_POST_LIST", {
            projectId: this.projectId,
            boardId: 1065,
            conditions: [{ key: "body.videoPostId", operator: "=", value: videoPostId }],
          });
          if (list.data.length > 0) {
            for (const item of list.data) {
              await this.$store
                .dispatch("board/DELETE_POST", {
                  projectId: this.projectId,
                  boardId: 1065,
                  postId: item.id,
                })
                .catch((err) => {
                  console.error("수강내역 삭제 실패", err);
                });
            }
          }

          // 교육 수강 삭제
          const res = await this.$store.dispatch("board/DELETE_POST", {
            projectId: this.projectId,
            boardId: this.boardId,
            postId: data.id,
          });

          if (res) {
            this.$refs.DetailPage.pause();
            this.openPostDetail = false;
            await this.$nextTick();
            await this.loadPostList();
          }
        },
      });
    },
    async closePopup() {
      // 현재까지 시청정보를 기록한다.
      await this.$refs.DetailPage.end();
    },
  },
};
</script>

<style scoped lang="scss">
// PC 레이아웃
.desktop-layout {
  display: block;

  @media (max-width: 768px) {
    display: none !important;
    margin: 0 16px; // 혹시 표시될 경우를 위한 여백
  }
}

// 검색 영역 모바일 여백
.search-section {
  @media (max-width: 768px) {
    width: calc(100% - 32px);
    margin: 0 16px; // 모바일에서 검색 영역 좌우 여백 추가
  }
}

// 헤더 영역 모바일 여백
.header-section {
  @media (max-width: 768px) {
    padding: 0 16px; // 모바일에서 헤더 영역 좌우 여백 추가
  }
}

// 모바일 레이아웃
.mobile-layout {
  display: none;

  @media (max-width: 768px) {
    display: block;
    padding: 0 16px; // 페이지 양옆 여백 추가
  }
}

// 모바일 필터
.mobile-filter {
  margin: 0 0 16px 0; // 하단 여백만 추가

  .mobile-select {
    padding: 8px 12px;
    border: 1px solid #e0e0e0;
    border-radius: 6px;
    background: white;
    font-size: 14px;
    min-width: 100px;
  }
}

// 모바일 카드 컨테이너
.mobile-cards-container {
  position: relative;
  overflow: hidden;
  margin-top: 16px;
}

.mobile-cards-wrapper {
  display: flex;
  transition: transform 0.3s ease;
  width: calc(100% * var(--card-count, 1));
}

.mobile-card {
  flex: 0 0 calc(100%); // 좌우 여백을 위해 width 조정
  padding: 20px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:active {
    transform: scale(0.98);
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.12);
  }
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;

  .card-number {
    background: #7e72f2;
    color: white;
    font-size: 12px;
    font-weight: bold;
    padding: 4px 8px;
    border-radius: 12px;
    min-width: 32px;
    text-align: center;
  }

  .card-target {
    background: #f0f0f0;
    color: #666;
    font-size: 12px;
    padding: 4px 8px;
    border-radius: 8px;
    font-weight: 500;
  }
}

.card-title {
  font-size: 18px;
  font-weight: 600;
  color: #1d1d1d;
  margin-bottom: 16px;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.card-info {
  margin-bottom: 16px;

  .info-row {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 10px;
    font-size: 14px;

    .info-icon {
      color: #7e72f2;
      flex-shrink: 0;
    }

    .info-label {
      color: #666;
      font-weight: 500;
      flex-shrink: 0;
      min-width: 70px;
    }

    .info-value {
      color: #333;
      font-weight: 400;
      flex: 1;
    }

    &:last-child {
      margin-bottom: 0;
    }
  }
}

.card-files {
  border-top: 1px solid #f0f0f0;
  padding-top: 12px;

  .files-header {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 14px;
    margin-bottom: 8px;

    .files-label {
      color: #666;
      font-weight: 500;
    }

    .files-count {
      color: #999;
      font-weight: 400;
      font-size: 13px;
    }
  }

  .files-list {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  .file-item {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    color: #7e72f2;
    padding: 6px 8px;
    background: #f8f7ff;
    border-radius: 6px;
    cursor: pointer;
    transition: background-color 0.2s ease;

    &:active {
      background: #ede8ff;
    }

    span {
      flex: 1;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }

  .file-more {
    font-size: 12px;
    color: #999;
    text-align: center;
    padding: 4px;
  }
}

// 카드 인디케이터
.card-indicators {
  display: flex;
  justify-content: center;
  gap: 8px;
  margin: 20px 0;
  padding: 0;

  .indicator {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #d0d0d0;
    cursor: pointer;
    transition: all 0.2s ease;

    &.active {
      background: #7e72f2;
      transform: scale(1.2);
    }
  }
}

// 더보기 버튼
.load-more-container {
  margin: 24px 0;
  text-align: center;

  .load-more-btn {
    min-height: 44px;
    padding: 12px 24px;
    font-size: 16px;
    font-weight: 500;
  }
}

// 빈 상태
.empty-state {
  margin: 16px 0 40px 0; // 상하 여백 조정

  .empty-content {
    text-align: center;
    padding: 40px 20px;

    .empty-icon {
      color: #d0d0d0;
      margin-bottom: 16px;
    }

    h3 {
      font-size: 18px;
      font-weight: 600;
      color: #666;
      margin-bottom: 8px;
    }

    p {
      font-size: 14px;
      color: #999;
    }
  }
}

// 기존 PC 스타일 유지
.sort-active {
  text-decoration: underline;
  color: #1d1d1d;
}

.body-table::v-deep {
  .vx-card__collapsible-content .vx-card__body {
    padding: 0;
  }
}

.body-table {
  table {
    border-collapse: collapse;
    border-top: 1px solid #cdcdcd;

    th {
      background-color: #f9f9f9;
      text-align: center;
      padding: 1rem;
      font-weight: 500;
      border-bottom: 1px solid #cdcdcd;
    }

    td {
      text-align: center;
      padding: 1rem;
      font-weight: 400;
      color: #3c3c3c;
      border-bottom: 1px solid #cdcdcd;
    }
  }
}

.btn {
  padding: 4px 7px;
  border-radius: 4px;
  color: #fff;

  &.success {
    background-color: #28c76f;
  }
  &.danger {
    background-color: #ea5455;
  }
  &.darken {
    background-color: #696d71;
  }
}

#detailPopup::v-deep {
  .vs-popup {
    width: 90%;
    max-width: 1200px;
  }
}

// 인쇄 모드에서는 PC 레이아웃 강제 표시
@media print {
  .desktop-layout {
    display: block !important;
  }

  .mobile-layout {
    display: none !important;
  }
}
</style>
